/** @format */

import React, { useState } from "react";
import { Container, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { colors } from "../../utility/types/constant";
import { formatCurrency } from "../../utility/currencyUtils";
import {
	VisualizationContainer,
	SectionTitle,
	SectionSubtitle,
	CircleDemo,
	MemberDot,
	CenterPot,
	PotAmount,
	Legend,
	LegendItem,
	LegendDot,
	LegendText,
	InfoPanel,
	InfoTitle,
	InfoText,
} from "./styles";

const CircleVisualization = () => {
	const { t } = useTranslation("home");
	const [activeMember, setActiveMember] = useState(0);
	const [showContributions, setShowContributions] = useState(true);
	const [contributingMembers, setContributingMembers] = useState<number[]>([]);

	const members = [
		{
			id: 1,
			name: "Sarah Chen",
			avatar: "/assets/images/avatars/avatar_1.jpg",
			status: "received",
		},
		{
			id: 2,
			name: "Marcus Johnson",
			avatar: "/assets/images/avatars/avatar_2.jpg",
			status: "current",
		},
		{
			id: 3,
			name: "Aisha Patel",
			avatar: "/assets/images/avatars/avatar_3.jpg",
			status: "waiting",
		},
		{
			id: 4,
			name: "David Kim",
			avatar: "/assets/images/avatars/avatar_4.jpg",
			status: "waiting",
		},
		{
			id: 5,
			name: "Elena Rodriguez",
			avatar: "/assets/images/avatars/avatar_5.jpg",
			status: "waiting",
		},
		{
			id: 6,
			name: "James Wilson",
			avatar: "/assets/images/avatars/avatar_6.jpg",
			status: "waiting",
		},
	];

	// Calculate values based on $50 contribution per member
	const contributionPerMember = 50;
	const totalMembers = members.length;
	const totalPool = contributionPerMember * totalMembers;

	React.useEffect(() => {
		const cycleInterval = setInterval(() => {
			// Phase 1: Show contributions (extended for smoother animation)
			setShowContributions(true);
			setContributingMembers([]);

			// Stagger member contributions with slower timing
			members.forEach((_, index) => {
				setTimeout(() => {
					setContributingMembers((prev) => [...prev, index]);
				}, index * 400);
			});

			// Phase 2: After contributions are complete, show payout (3 seconds later)
			setTimeout(() => {
				setShowContributions(false);
				setActiveMember((prev) => (prev + 1) % members.length);
			}, 3000);
		}, 6500); // Complete cycle every 6.5 seconds

		return () => clearInterval(cycleInterval);
	}, [members.length]);

	return (
		<VisualizationContainer>
			<Container maxWidth="xl">
				<Box sx={{ textAlign: "center", mb: 8 }} data-aos="fade-up">
					<SectionTitle>{t("circleVisualization.title")}</SectionTitle>
					<SectionSubtitle>
						{t("circleVisualization.subtitle")}
					</SectionSubtitle>
				</Box>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
						gap: 6,
						alignItems: "center",
					}}>
					<CircleDemo data-aos="zoom-in">
						<CenterPot data-aos="pulse">
							<PotAmount>{formatCurrency(totalPool)}</PotAmount>
							<Box
								sx={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.9)" }}>
								{t("circleVisualization.monthlyPool")}
							</Box>
						</CenterPot>

						{members.map((member, index) => {
							const angle =
								(index / members.length) * 2 * Math.PI - Math.PI / 2;
							const radius = 140;
							const x = Math.cos(angle) * radius;
							const y = Math.sin(angle) * radius;

							return (
								<MemberDot
									key={member.id}
									style={{
										left: `calc(50% + ${x}px)`,
										top: `calc(50% + ${y}px)`,
									}}
									isActive={index === activeMember}
									hasReceived={index < activeMember}
									data-aos="fade-in"
									data-aos-delay={index * 100}>
									<Box
										component="img"
										src={member.avatar}
										alt={member.name}
										sx={{
											width: "100%",
											height: "100%",
											objectFit: "cover",
											borderRadius: "50%",
										}}
									/>
									<Box
										sx={{
											fontSize: "0.7rem",
											mt: 0.5,
											whiteSpace: "nowrap",
											fontWeight: 600,
											textShadow: "0 1px 3px rgba(0,0,0,0.3)",
										}}>
										{member.name}
									</Box>
								</MemberDot>
							);
						})}

						{/* Contribution Animations */}
						{showContributions &&
							contributingMembers.map((memberIndex) => {
								const angle =
									(memberIndex / members.length) * 2 * Math.PI - Math.PI / 2;
								const radius = 140;
								const x = Math.cos(angle) * radius;
								const y = Math.sin(angle) * radius;

								return (
									<Box
										key={`contribution-${memberIndex}`}
										sx={{
											position: "absolute",
											left: `calc(50% + ${x}px)`,
											top: `calc(50% + ${y}px)`,
											transform: "translate(-50%, -50%)",
											fontSize: "1.5rem",
											fontWeight: 700,
											color: colors.mainSecondary,
											animation: "contributeToCenter 1.8s ease-in-out forwards",
											pointerEvents: "none",
											"@keyframes contributeToCenter": {
												"0%": {
													transform: "translate(-50%, -50%) scale(0)",
													opacity: 0,
												},
												"40%": {
													transform: "translate(-50%, -50%) scale(1.2)",
													opacity: 1,
												},
												"100%": {
													transform: `translate(calc(-50% + ${-x}px), calc(-50% + ${-y}px)) scale(0.5)`,
													opacity: 0,
												},
											},
										}}>
										+{formatCurrency(contributionPerMember)}
									</Box>
								);
							})}

						{/* Payout Animation */}
						{!showContributions && (
							<Box
								sx={{
									position: "absolute",
									left: "50%",
									top: "50%",
									transform: "translate(-50%, -50%)",
									fontSize: "2rem",
									fontWeight: 800,
									color: colors.primary,
									animation: "payoutPulse 1.4s ease-in-out",
									pointerEvents: "none",
									textShadow: "0 4px 12px rgba(4, 13, 69, 0.3)",
									"@keyframes payoutPulse": {
										"0%": {
											transform: "translate(-50%, -50%) scale(0.5)",
											opacity: 0,
										},
										"50%": {
											transform: "translate(-50%, -50%) scale(1.3)",
											opacity: 1,
										},
										"100%": {
											transform: "translate(-50%, -50%) scale(1)",
											opacity: 0.8,
										},
									},
								}}>
								💰
							</Box>
						)}
					</CircleDemo>

					<Box>
						<InfoPanel data-aos="fade-left">
							<InfoTitle>{t("circleVisualization.howItWorks")}</InfoTitle>
							<InfoText>
								<strong>{t("circleVisualization.example")}</strong>
								<br />
								<br />
								<strong>{t("circleVisualization.step1")}</strong>
								<br />
								{t("circleVisualization.step1Desc")}
								<br />
								<br />
								<strong>{t("circleVisualization.step2")}</strong>
								<br />
								{t("circleVisualization.step2Desc")}
								<br />
								<br />
								<strong>{t("circleVisualization.step3")}</strong>
								<br />
								{t("circleVisualization.step3Desc")}
								<br />
								<br />
								<em>
									{t("circleVisualization.tagline")}
								</em>
							</InfoText>
						</InfoPanel>

						<Legend>
							<LegendItem>
								<LegendDot status="active" />
								<LegendText>{t("circleVisualization.receiving")}</LegendText>
							</LegendItem>
							<LegendItem>
								<LegendDot status="received" />
								<LegendText>{t("circleVisualization.received")}</LegendText>
							</LegendItem>
							<LegendItem>
								<LegendDot status="waiting" />
								<LegendText>{t("circleVisualization.awaiting")}</LegendText>
							</LegendItem>
						</Legend>
					</Box>
				</Box>
			</Container>
		</VisualizationContainer>
	);
};

export default CircleVisualization;
